import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
  setDoc,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";
import { COLLECTIONS } from "../constant/dbCollections";
import { Payout, PayoutAccount } from "../types";
import { db } from "@firebase";

class PayoutMethods {
  async getAllPayouts(): Promise<Payout[]> {
    try {
      const payoutsCollection = collection(db, COLLECTIONS.PAYOUTS);
      const snapshot = await getDocs(payoutsCollection);

      return snapshot.docs.map((doc) => {
        const data = doc.data() as Payout;
        return {
          ...data,
          id: doc.id, // Add the document ID
        };
      });
    } catch (error) {
      console.error("Error fetching payouts:", error);
      throw error;
    }
  }

  async getPayoutsByUserId(userId: string): Promise<PayoutAccount[]> {
    try {
      const payoutsQuery = query(
        collection(db, COLLECTIONS.PAYOUTS),
        where("userId", "==", userId)
      );
      const snapshot = await getDocs(payoutsQuery);

      return snapshot.docs.map((doc) => {
        const data = doc.data() as PayoutAccount;
        return {
          ...data,
          id: doc.id, // Include Firestore document ID
        };
      });
    } catch (error) {
      console.error("Error fetching payouts by userId:", error);
      throw error;
    }
  }

  async createPayout(
    payout: Partial<PayoutAccount> & { userId: string }
  ): Promise<void> {
    try {
      const payoutsCollection = collection(db, COLLECTIONS.PAYOUTS);

      // Generate a document reference with an ID
      const newDocRef = doc(payoutsCollection);
      const newId = newDocRef.id; // Extract the generated ID

      await setDoc(newDocRef, {
        ...payout,
        id: newId,
        userId: payout.userId, // Ensure user ID is included
        type: "bank",
        isDefault: false,
        isActive: false,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      console.log("Payout created successfully");
    } catch (error) {
      console.error("Error creating payout:", error);
      throw error;
    }
  }

  async updatePayout(
    id: string,
    updatedFields: Partial<PayoutAccount>
  ): Promise<void> {
    try {
      const payoutDoc = doc(db, COLLECTIONS.PAYOUTS, id);
      await updateDoc(payoutDoc, {
        ...updatedFields,
        updatedAt: Timestamp.now(),
      });
      console.log("Payout account updated successfully!");
    } catch (error) {
      console.error("Error updating payout:", error);
      throw error;
    }
  }

  async deletePayout(id: string): Promise<void> {
    try {
      const payoutDoc = doc(db, COLLECTIONS.PAYOUTS, id);
      await deleteDoc(payoutDoc);
    } catch (error) {
      console.error("Error deleting payout:", error);
      throw error;
    }
  }

  // Fetch purchases where the logged-in user is the seller/creator
  // async getPurchasesBySellerId(sellerId: string): Promise<any[]> {
  //   try {
  //     // Create a DocumentReference for the seller
  //     const sellerRef = doc(db, `users/${sellerId}`);

  //     const purchasesQuery = query(
  //       collection(db, COLLECTIONS.PURCHASES),
  //       where("creatorId", "==", sellerRef) // Compare using DocumentReference
  //     );
  //     const snapshot = await getDocs(purchasesQuery);

  //     return snapshot.docs.map((doc) => ({
  //       id: doc.id,
  //       ...doc.data(),
  //     }));
  //   } catch (error) {
  //     console.error("Error fetching purchases by sellerId:", error);
  //     throw error;
  //   }
  // }
  async getPurchasesBySellerId(sellerId: string): Promise<any[]> {
    try {
      const sellerRef = doc(db, `users/${sellerId}`);
      const purchasesQuery = query(
        collection(db, COLLECTIONS.PURCHASES),
        where("creatorId", "==", sellerRef)
      );
      const snapshot = await getDocs(purchasesQuery);

      const purchases = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const purchase = docSnap.data();
          const id = docSnap.id;

          let contentData = null;
          let creatorData = null;

          try {
            if (purchase.contentId) {
              const contentSnap = await getDoc(purchase.contentId);
              contentData = contentSnap.exists() ? contentSnap.data() : null;
            }

            if (purchase.creatorId) {
              const creatorSnap = await getDoc(purchase.creatorId);
              creatorData = creatorSnap.exists() ? creatorSnap.data() : null;
            }
          } catch (error) {
            console.error("Error fetching referenced docs:", error);
          }

          return {
            id,
            ...purchase,
            contentData, // resolved content object
            creatorData, // resolved creator object
          };
        })
      );

      return purchases;
    } catch (error) {
      console.error("Error fetching purchases by sellerId:", error);
      throw error;
    }
  }

  // update creator balance
  // ✅ Fetch and update user balance from completed purchases
  async updateUserBalanceFromPurchases(userId: string) {
    try {
      // ✅ Fetch all completed purchases for this seller
      const purchases = await this.getPurchasesBySellerId(userId);

      // ✅ Calculate total earnings from purchases
      const totalEarnings = purchases.reduce(
        (sum, purchase) => sum + (purchase.amount || 0),
        0
      );

      // ✅ Update the user's balance in Firestore
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, {
        balance: totalEarnings,
        updatedAt: serverTimestamp(), // ✅ Add updated timestamp
      });
    } catch (error) {
      console.error("Error updating user balance:", error);
    }
  }
}

const PayoutService = new PayoutMethods();
export { PayoutService };
